import React, { HTMLAttributes } from 'react';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Paragraph } from '@uncinc/react-drupal-core';
import { useTranslation } from 'react-i18next';
import Immutable, { fromJS } from 'immutable';
import { Container, Icon } from '@uncinc/uncinc-react-kitchen-sink';
import type { NodeAvOProject as Type } from '../../../types/uncinc_profile/nodes';
import usePhaseState from '../../../helpers/usePhaseState';
import {
  AVO_PHASE_BEFORE,
  AVO_PHASE_DURING,
  AVO_PHASE_AFTER,
  APP_OPTIONS,
  SCREEN_MD,
  SCREEN_LG,
  SCREEN_XL,

} from '../../../config/constants';
import {
  WysiwygField, Share, ButtonWrapper, VideoPopup, Breadcrumbs, DynamicMediaItem,
} from '../../../components';

import './style.scss';

interface AvOProjectProps
  extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const NodeAvOProject: React.FC<AvOProjectProps> = ({ entity }) => {
  const { t } = useTranslation();
  const page = entity.toJS();
  const paragraphs = page.field_paragraphs;
  const salesForceId = page.field_sf_project_id[0]?.value;
  const video = page.field_video[0]?.entity.field_media_video_embed_field[0]?.value;
  const image = page.field_media_item[0];
  const title = page.title[0]?.value;
  const body = page.body[0]?.value;
  const province = page.field_province[0]?.entity.name[0]?.value;
  const firstName = page.field_contact_first_name[0]?.value;
  const surName = page.field_contact_last_name[0]?.value;
  const organization = page.field_avo_organization[0]?.value;
  const voteCount = page.field_vote_count[0]?.value;
  const phase = usePhaseState();
  const rankingPlace = page.field_rank[0]?.value ?? 0;
  const phaseDuringVoting = phase === AVO_PHASE_DURING;
  const rankingPrefix = rankingPlace < 20 ? 'de' : 'e';
  const phaseLabel = {
    [AVO_PHASE_BEFORE]: t('avo.votes_not_open_state'),
    [AVO_PHASE_DURING]: (
      <>
        {t('avo.organization_votes_count', { organization })}
        <span className="icon AvOProjectPage-iconInText icon-apple icon-variant-4" />{' '}
        {voteCount} {t('avo.votings')}
      </>
    ),
    [AVO_PHASE_AFTER]: (
      <>
        {t('avo.votes_place_after_voting', { organization })}
        <span className="icon AvOProjectPage-iconInText icon-apple icon-variant-4" />{' '}
        {rankingPlace}
        {rankingPrefix}{' '}
        {t('avo.place')}
      </>
    ),
  };

  const fieldImageSrc = {
    [SCREEN_MD]: image?.styles.ratio_16_9_md,
    [SCREEN_LG]: image?.styles.ratio_16_9_lg,
    [SCREEN_XL]: image?.styles.ratio_16_9_xl,
  };

  const breadcrumbs = [
    {
      title: t('home'),
      url: '/',
    },
    {
      title: t('avo.projects'),
      url: APP_OPTIONS?.parameters.avoProjectOverviewUri,
    },
  ];

  return (
    <article className="AvOProjectPage">
      <Container>
        {/* Breadcrumb navigation */}
        <div className="row">
          <div className="col-12">
            <Breadcrumbs
              includeCurrent
              hideHome={false}
              // @ts-expect-error
              customCrumbs={fromJS(breadcrumbs)}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {video ? (
              <div className="AvOProjectPage__hero">
                <VideoPopup
                  className="AvOProjectPage__hero__video"
                  thumbnail={fieldImageSrc}
                  title={title}
                  embedUrl={video}
                  showIcon={false}
                />
              </div>
            ) : image && (
              <DynamicMediaItem
                // @ts-ignore
                media={image}
                imageAspectRatio="16_9"
              />
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
            <div className="AvOProjectPage__infoBlock">
              <h1>{title}</h1>
              <WysiwygField
                property="schema:text"
                className="AvOProjectPage__body"
                html={body}
              />
              {phaseDuringVoting && (
                <ButtonWrapper
                  href="#voteBlock"
                  className="AvOProjectPage__voteButton"
                >
                  {t('avo.vote_now')}
                  <span className="icon icon-apple icon-variant-4" />
                  {voteCount}
                </ButtonWrapper>
              )}
            </div>
          </div>
        </div>

        <div className="AvOProjectPage__content">
          <div className="row">
            <div className="col-md-10 offset-md-1 col-lg-3 offset-lg-0 AvOProjectPage__infoItems">
              <span className="AvOProjectPage__infoItems__infoItem">
                <Icon name="location_on" flair="filled" />
                {province}
              </span>
              <span className="AvOProjectPage__infoItems__infoItem">
                <Icon name="person" flair="filled" />
                {`${firstName} ${surName}`}
              </span>
              <span className="AvOProjectPage__infoItems__infoItem">
                <Icon name="people" flair="filled" />
                {organization}
              </span>
            </div>

            <div className="col-md-10 offset-md-1 col-lg-8 offset-lg-0">
              {paragraphs.length > 0 && (
                <div className="AvOProjectPage__paragraphs">
                  {paragraphs.map((item) => {
                    const paragraph = item.entity;
                    const paragraphId = item.target_id;

                    return (
                      <Paragraph
                        key={paragraphId}
                        entity={Immutable.fromJS(paragraph)}
                        id={paragraphId}
                      />
                    );
                  })}
                </div>
              )}
              <div className="AvOProjectPage__share">
                <Share />
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12">
            <div className="AvOProjectPage__voteBlock" id="voteBlock">
              <div className="AvOProjectPage__voteBlock__votePlaceBlock">
                {phaseLabel[phase]}
              </div>
              <div className="AvOProjectPage__voteBlock__voteTitleBlock">
                <h4>
                  {phaseDuringVoting
                    ? t('avo.organization_deserves_apple', { organization })
                    : organization}
                </h4>
                {phaseDuringVoting && (
                  <ButtonWrapper
                    href={`${APP_OPTIONS.parameters.avoVoteUrl}?projectCode=${salesForceId}&projectName=${organization}`}
                    inverted
                    className="AvOProjectPage__voteBlock__linkToVote"
                  >
                    {t('avo.vote_for_this_winning_change')}
                  </ButtonWrapper>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </article>
  );
};

export default NodeAvOProject;
